import { faBook, faClose, faPen, faPlus, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { IPlatformWhatsAppKnowledge } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppKnowledge"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

type Props = {
  documents: IPlatformWhatsAppKnowledge[]
  onAdd: () => void
  onEdit: (document: IPlatformWhatsAppKnowledge) => void
  onDelete: (documentId: number) => Promise<void>
  onClose: () => void
}

export const PlatformWhatsAppKnowledgeListPopup = ({ documents, onAdd, onEdit, onDelete, onClose }: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null)

  const deleteDocument = async (documentId: number) => {
    if (deletingDocumentId !== null) return

    setDeletingDocumentId(documentId)

    try {
      await onDelete(documentId)
    } catch {
      return
    } finally {
      setDeletingDocumentId(null)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("knowledge.titleSection")}
              icon={faBook}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGPlusColor}
                iconColor={THEME.colors.buttonFGPlusColor}
                icon={faPlus}
                onClick={onAdd}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgeListPopup.button.onAdd"
              />

              <GetSpace left={5} />

              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgeListPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <div className="flex max-h-[75dvh] min-h-0 flex-col overflow-hidden p-4">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
                {documents.length === 0 ? (
                  <div className="flex min-h-52 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-slate-500">
                    <FontAwesomeIcon icon={faBook} className="mb-3 text-3xl text-slate-300" />

                    <p className="text-sm font-semibold">{t("knowledge.empty")}</p>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {documents.map((document, index) => (
                      <Animate
                        key={`${document.isSystem}-${document.id}-${index}`}
                        type={AnimationTypes.fadeInTraslate}
                        duration="1s"
                        delay={`${Math.min(index, 8) * 0.04}s`}
                      >
                        <article className="flex min-w-0 flex-col rounded-2xl border-2 border-orange-100 bg-gradient-to-br from-white via-orange-50/30 to-white p-4 shadow-sm transition-all duration-200 hover:border-orange-200 hover:shadow-md">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <strong className="block wrap-break-word text-sm text-slate-900">{document.title}</strong>

                              <p className="mt-1 text-xs font-semibold text-slate-500">{document.category}</p>
                            </div>

                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <span
                                className={`rounded-full px-2 py-1 text-[10px] font-bold ${document.isSystem ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}
                              >
                                {document.isSystem ? t("knowledge.system") : t("knowledge.custom")}
                              </span>

                              <span
                                className={`rounded-full px-2 py-1 text-[10px] font-bold ${document.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                              >
                                {document.isActive ? t("knowledge.active") : t("knowledge.inactive")}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
                            <span className="rounded-lg border border-slate-200 bg-white px-2 py-1">
                              {t("knowledge.priority")}: {document.priority}
                            </span>

                            {document.keywords.trim() && (
                              <span className="max-w-full truncate rounded-lg border border-slate-200 bg-white px-2 py-1">
                                {document.keywords}
                              </span>
                            )}
                          </div>

                          <details className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                            <summary className="cursor-pointer select-none font-bold text-indigo-700">
                              {t("knowledge.viewContent")}
                            </summary>

                            <p className="mt-3 whitespace-pre-wrap wrap-break-word leading-5">{document.content}</p>
                          </details>

                          {!document.isSystem && (
                            <div className="mt-auto flex gap-2 pt-3">
                              <button
                                type="button"
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 transition hover:bg-indigo-100"
                                onClick={() => onEdit(document)}
                                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgeListPopup.button.onEditDocument"
                              >
                                <FontAwesomeIcon icon={faPen} />
                                {t("actions.edit")}
                              </button>

                              <button
                                type="button"
                                disabled={deletingDocumentId !== null}
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-wait disabled:opacity-60"
                                onClick={() => deleteDocument(document.id)}
                                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppKnowledgeListPopup.button.deleteDocumentDocumentId"
                              >
                                <FontAwesomeIcon
                                  icon={deletingDocumentId === document.id ? faSpinner : faTrash}
                                  spin={deletingDocumentId === document.id}
                                />
                                {t("actions.delete")}
                              </button>
                            </div>
                          )}
                        </article>
                      </Animate>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
